/**
 * Conduit bending reference data (Ugly's Electrical References).
 *
 * Offset bends: the "multiplier" times the desired offset depth gives the
 * distance between the two bend marks. "shrinkPerInch" times the offset depth
 * gives the amount of conduit gained/consumed by the offset (used to adjust
 * the overall measurement).
 *
 * These are bending-trade constants, not NEC code values, but they're the
 * standard published figures every electrician's reference uses.
 */
export const OFFSET_ANGLES = [10, 22.5, 30, 45, 60] as const;
export type OffsetAngle = (typeof OFFSET_ANGLES)[number];

export const OFFSET_MULTIPLIER: Record<OffsetAngle, number> = {
  10: 6.0,
  22.5: 2.6,
  30: 2.0,
  45: 1.4,
  60: 1.2,
};

/** Shrink (inches of conduit consumed) per inch of offset depth. */
export const OFFSET_SHRINK_PER_INCH: Record<OffsetAngle, number> = {
  10: 1 / 16, // 0.0625
  22.5: 3 / 16, // 0.1875
  30: 1 / 4, // 0.25
  45: 3 / 8, // 0.375
  60: 1 / 2, // 0.5
};

/**
 * 90° stub-up "take-up" (deduct) figures by conduit trade size, in inches.
 * Bend mark = desired stub height − take-up. Common hand-bender values.
 */
export const STUB_TAKE_UP_IN: Record<string, number> = {
  "1/2": 5,
  "3/4": 6,
  "1": 8,
  "1-1/4": 11,
};

/**
 * Three-point saddle: center bend is 45°, two outer bends are 22.5°.
 * Distance from the center mark to each outer mark = 2.5 × obstruction height.
 * Shrink ≈ 3/16" per inch of obstruction height.
 */
export const SADDLE_CENTER_TO_OUTER_MULTIPLIER = 2.5;
export const SADDLE_SHRINK_PER_INCH = 3 / 16;
